import { createGrid } from "ag-grid-community";
import { Controller } from "@hotwired/stimulus";
import { columnDefsTheme } from '../ag_grid/theme';

// Stimulus controller: column-definitions-grid
// Renders an AG Grid for the Column Definitions list with read-only/edit modes.
export default class extends Controller {
  static targets = ["container", "error", "requiresDeploymentChip", "deployButton", "promotionStatus", "promoteLink", "promoteDisabled"]
  static values = {
    rows: Array,
    tableDefinitionId: Number,
    editable: { type: Boolean, default: false },
    // [{ id: Number, label: String }]
    lookupOptions: Array,
    requiresDeployment: Boolean
  }

  // Build a simple popup editor with an input and a dropdown list of options.
  // Displays option labels; returns the selected option id (or null) as the cell value.
  makeLookupTableEditor() {
    const self = this
    return class LookupTableEditor {
      init(params) {
        this.params = params
        this.eGui = document.createElement('div')
        this.eGui.className = 'p-2'

        const container = document.createElement('div')
        container.className = 'flex flex-col gap-2'

        const input = document.createElement('input')
        input.type = 'text'
        input.className = 'w-72 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'
        input.placeholder = 'Search tables…'

        const listWrap = document.createElement('div')
        listWrap.className = 'max-h-60 overflow-auto rounded border border-gray-200'

        const list = document.createElement('ul')
        list.className = 'divide-y divide-gray-100'
        listWrap.appendChild(list)

        const opts = Array.isArray(self.lookupOptionsValue) ? self.lookupOptionsValue : []
        const labelFor = (id) => {
          const it = opts.find(o => String(o.id) === String(id))
          return it ? it.label : ''
        }

        // current value label
        const currentId = params.value
        input.value = currentId ? labelFor(currentId) : ''

        const render = (filterText = '') => {
          list.innerHTML = ''
          const f = filterText.trim().toLowerCase()
          const items = f ? opts.filter(o => o.label.toLowerCase().includes(f)) : opts
          items.slice(0, 200).forEach((o) => {
            const li = document.createElement('li')
            li.className = 'px-2 py-1 text-sm cursor-pointer hover:bg-indigo-50'
            li.textContent = o.label
            li.dataset.id = o.id
            li.addEventListener('mousedown', (e) => {
              e.preventDefault()
              this._selectedId = o.id
              params.stopEditing()
            })
            list.appendChild(li)
          })
        }

        input.addEventListener('input', () => render(input.value))
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.stopPropagation()
            params.stopEditing(true)
          }
          if (e.key === 'Enter') {
            // If the input matches exactly one option, select it; else clear
            const match = (Array.isArray(self.lookupOptionsValue) ? self.lookupOptionsValue : []).find(o => o.label === input.value)
            this._selectedId = match ? match.id : null
            params.stopEditing()
          }
        })

        container.appendChild(input)
        container.appendChild(listWrap)
        this.eGui.appendChild(container)

        render(input.value)
      }

      getGui() { return this.eGui }
      afterGuiAttached() { this.eGui.querySelector('input')?.focus() }
      getValue() {
        // Return the selected id (or null). If user typed arbitrary text not matching, treat as null.
        if (typeof this._selectedId === 'undefined') return this.params.value ?? null
        return this._selectedId ?? null
      }
      isPopup() { return true }
      destroy() { /* noop */ }
    }
  }

  connect() {
    try {
      console.log('[column-definitions-grid] connect, rows:', Array.isArray(this.rowsValue) ? this.rowsValue.length : 'n/a', 'editable:', this.editableValue)
      // Ensure container target is available before initializing
      if (!this.hasContainerTarget) {
        console.error('[column-definitions-grid] Container target not found')
        this.showError('Missing target element "container" for "column-definitions-grid" controller')
        return
      }
      this.initializeGrid()
    } catch (e) {
      this.showError(e.message)
    }
  }

  disconnect() {
    if (this.gridApi) this.gridApi.destroy()
  }

  initializeGrid() {
    const gridOptions = {
      theme: columnDefsTheme,
      columnDefs: this.getColumnDefs(),
      defaultColDef: {
        resizable: true,
        sortable: false,
        filter: true,
        filterParams: {
          caseSensitive: false,
          buttons: ['reset']
        },
        editable: (p) => this.isEditable(p),
        // style deleted rows: strike-through for string fields, gray for others
        cellClass: (p) => {
          if (p?.data?.is_deleted) {
            return this.isStringField(p.colDef?.field) ? 'line-through text-gray-400' : 'text-gray-400'
          }
          return undefined
        }
      },
      components: {
        lookupTableEditor: this.makeLookupTableEditor()
      },
      // Enable single-click to edit when in edit mode
      singleClickEdit: !!this.editableValue,
      // Use Infinite Row Model in read-only mode for paging, Client-Side in edit mode to allow reordering
      rowModelType: this.editableValue ? undefined : 'infinite',
      cacheBlockSize: this.editableValue ? undefined : 100,
      maxBlocksInCache: this.editableValue ? undefined : 2,
      rowDragManaged: true,
      animateRows: true,
      suppressMoveWhenRowDragging: false,
      getRowId: (params) => {
        if (params?.data?.id != null) return String(params.data.id)
        // Ensure a stable ID for pinned rows to avoid AG Grid warning #25
        if (params?.node?.rowPinned) return `pinned-${params.node.rowPinned}`
        return undefined
      },
      onGridReady: (params) => {
        this.gridApi = params.api
        this.columnApi = params.columnApi
        if (this.editableValue) {
          // Client-side: load all rows once
          this.loadAllRowsForEditing()
          this.gridApi.setGridOption('pinnedTopRowData', [{}])
        } else {
          // Read-only: Infinite datasource
          this.gridApi.setGridOption('datasource', this.getDataSource())
        }
      },
      onRowDragEnd: (event) => this.handleRowReorder(event),
      onCellValueChanged: (event) => this.handleCellUpdate(event),
      // Refresh the add (+) icon state after edits in the pinned row
      onCellEditingStopped: (event) => this.handlePinnedRowEditStopped(event),
      onCellClicked: (event) => this.onCellClicked(event)
    }

    try {
      createGrid(this.containerTarget, gridOptions)
    } catch (err) {
      console.error('[column-definitions-grid] createGrid failed:', err)
      this.showError(`Failed to initialize grid: ${err.message}`)
    }
  }

  getColumnDefs() {
    const lookupLabelFor = (id) => {
      if (!id) return ''
      const opts = Array.isArray(this.lookupOptionsValue) ? this.lookupOptionsValue : []
      const item = opts.find(o => String(o.id) === String(id))
      return item ? item.label : ''
    }
    const boolRenderer = (p) => {
      if (!p.value) return ''
      const cls = p.data?.is_deleted ? 'text-gray-400' : 'text-green-600'
      return `<span class="${cls}">&#10003;</span>`
    }

    const truncate = (s, n) => {
      if (s == null) return ''
      return s.length > n ? s.slice(0, n - 1) + '…' : s
    }

    const actionsRenderer = (p) => {
      if (!p.data || !p.data.id) return ''
      return `
        <div class="flex items-center gap-2">
          <button data-action="delete" class="text-red-600 hover:text-red-700">Delete</button>
        </div>
      `
    }

    return [
      { headerName: '', colId: 'drag', width: 44, minWidth: 40, rowDrag: false, headerTooltip: 'Add / Delete', sortable: false, filter: false, suppressMenu: true, editable: false, suppressNavigable: true,
        cellRenderer: (p) => {
          const wrap = document.createElement('div')
          wrap.className = 'h-full w-full flex items-center justify-center'
          if (p.node?.rowPinned === 'top') {
            const data = p.node?.data || {}
            const hasReq = !!data.name && !!data.data_type
            const ready = hasReq
            const btn = document.createElement('button')
            btn.type = 'button'
            btn.dataset.action = ready ? 'create' : ''
            btn.title = ready ? 'Create column definition' : 'Create Column Definition (Enter Name and Data Type to enable)'
            btn.className = ready ? 'text-green-600 hover:text-green-700' : 'text-gray-400'
            btn.disabled = !ready
            btn.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>`
            // Make the add button work with a single click even if an editor is open
            const triggerCreate = () => {
              // Guard against double-submits from rapid clicks
              // Commit edits so latest values are available
              if (this.gridApi?.stopEditing) this.gridApi.stopEditing(true)
              const rowData = p.node?.data || {}
              if (rowData.name && rowData.data_type) {
                // disable button during create
                const prevDisabled = btn.disabled
                btn.disabled = true
                btn.classList.add('opacity-50', 'cursor-not-allowed')
                Promise.resolve()
                  .then(() => this.createFromPinnedRow(rowData))
                  .finally(() => {
                    // Button state will be reset when pinned row resets; if not, restore prior disabled state
                    btn.disabled = prevDisabled
                    btn.classList.remove('opacity-50', 'cursor-not-allowed')
                  })
              } else {
                // Refresh icon state to reflect readiness
                if (this.gridApi?.refreshCells) this.gridApi.refreshCells({ force: true, columns: ['drag'] })
              }
            }
            // Use click only (avoid mousedown + click double-fire)
            btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); triggerCreate() })
            wrap.appendChild(btn)
            return wrap
          }
          // If deleted, show restore button; else show delete
          if (p.data?.is_deleted) {
            const restore = document.createElement('button')
            restore.type = 'button'
            restore.dataset.action = 'restore'
            const isReadOnly = !this.editableValue
            restore.className = isReadOnly ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'
            restore.title = isReadOnly ? 'Restore not available in view mode' : 'Restore column definition'
            restore.disabled = isReadOnly
            restore.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-5 h-5">
                <!-- outer circle -->
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 3a9 9 0 110 18 9 9 0 010-18z" />
                <!-- straight left arrow -->
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 12H9m0 0l2.5-2.5M9 12l2.5 2.5" />
              </svg>
            `
            wrap.appendChild(restore)
          } else {
            const del = document.createElement('button')
            del.type = 'button'
            del.dataset.action = 'delete'
            const isPk = !!p.data?.is_primary_key
            const isReadOnly = !this.editableValue
            const isDisabled = isPk || isReadOnly
            del.className = isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-700'
            del.title = isPk ? 'Cannot delete a primary key column' : (isReadOnly ? 'Delete not available in view mode' : 'Delete column definition')
            del.disabled = isDisabled
            del.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>`
            wrap.appendChild(del)
          }
          return wrap
        }
      },
      { headerName: 'Name', field: 'name', minWidth: 200, headerTooltip: 'Column name', editable: (p) => !!this.editableValue && !p.data?.is_deleted, rowDrag: this.editableValue, cellClass: 'text-left',
        cellRenderer: (p) => {
          const wrap = document.createElement('div')
          wrap.className = 'w-full flex items-center justify-start'
          const text = document.createElement('span')
          // For pinned add row, show placeholder when empty and not editing
          const isPinnedAdd = p.node?.rowPinned === 'top'
          const hasValue = !!(p.value && String(p.value).trim().length > 0)
          if (isPinnedAdd && !hasValue) {
            text.textContent = 'Add New Column Definition'
            text.className = 'text-gray-400 italic'
          } else {
            const isDeleted = !!p.data?.is_deleted
            // Display the full value without truncation
            text.textContent = (p.value == null ? '' : String(p.value))
            if (isDeleted) {
              text.className = 'line-through text-gray-400'
              text.title = 'deleted'
            }
          }
          wrap.appendChild(text)
          return wrap
        }
      },
      { headerName: 'Data Type', field: 'data_type', width: 130,
        cellClass: (p) => {
          if (p?.data?.is_deleted) return 'text-center line-through text-gray-400'
          if (p?.data?.foreign_key_table_id) return 'text-center text-gray-400 italic'
          return 'text-center'
        },
        editable: (p) => !!this.editableValue && !p.data?.is_deleted && !p.data?.foreign_key_table_id,
        cellEditor: this.editableValue ? 'agSelectCellEditor' : undefined,
        cellEditorParams: this.editableValue ? { values: [
          'varchar','character','text',
          'integer','smallint','bigint',
          'decimal','real','double',
          'boolean',
          'timestamp','timestamptz','time','timetz','date',
          'binary'
        ] } : undefined,
        tooltipValueGetter: (p) => p?.data?.foreign_key_table_id ? 'Data type is managed by the lookup table reference' : undefined
      },
      { headerName: 'Length', field: 'length', width: 110, 
        cellClass: (p) => p?.data?.is_deleted ? 'text-center text-gray-400' : 'text-center',
        editable: (p)=> this.editableValue && !p.data?.is_deleted && this.lengthEnabledFor(p.data?.data_type) },
      { headerName: 'Scale', field: 'scale', width: 100, 
        cellClass: (p) => p?.data?.is_deleted ? 'text-center text-gray-400' : 'text-center',
        editable: (p)=> this.editableValue && !p.data?.is_deleted && this.scaleEnabledFor(p.data?.data_type) },
      { headerName: 'Import Only', field: 'is_imported', width: 140, 
        cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center' }),
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',  
        editable: (p) => !!this.editableValue && !p.data?.is_deleted
      },
      { headerName: 'Primary Key', field: 'is_primary_key', width: 140, 
        cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center' }),
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        editable: (p) => !!this.editableValue && !p.data?.is_deleted
      },
      { headerName: 'Required', field: 'is_required', width: 120, 
        cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center' }),
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        editable: (p) => this.editableValue && !p.data?.is_primary_key && !p.data?.is_deleted
      },
      { headerName: 'Lookup Table', field: 'foreign_key_table_id', minWidth: 200,
        valueFormatter: (p) => lookupLabelFor(p.value),
        // Render label with strike-through when deleted
        cellRenderer: (p) => {
          const wrap = document.createElement('div')
          wrap.className = 'w-full flex items-center justify-start'
          const text = document.createElement('span')
          const label = lookupLabelFor(p.value)
          text.textContent = label
          if (p.data?.is_deleted && label) {
            text.className = 'line-through text-gray-400'
            text.title = 'deleted'
          }
          wrap.appendChild(text)
          return wrap
        },
        cellEditor: 'lookupTableEditor',
        cellEditorPopup: true,
        editable: (p) => this.editableValue && !p.data?.is_deleted && !p.data?.is_imported && !p.data?.is_primary_key && !(p.data?.valid_values && String(p.data.valid_values).trim().length > 0)
      },
      { headerName: 'Default', field: 'default', minWidth: 140, 
        cellClass: (p) => p?.data?.is_deleted ? 'text-center line-through text-gray-400' : 'text-center',
        editable: (p) => this.editableValue && !p.data?.is_imported && !p.data?.is_primary_key && !p.data?.is_deleted
      },
      // Added from original implementation: Valid Values, Display Length, Description
      { headerName: 'Valid Values', field: 'valid_values', minWidth: 200, headerTooltip: 'Comma-separated allowed values',
        valueFormatter: p => truncate(p.value, 30),
        cellEditor: 'agLargeTextCellEditor', cellEditorPopup: true,
        cellEditorParams: { rows: 4, cols: 60 },
        editable: (p) => this.editableValue && !p.data?.is_imported && !p.data?.is_primary_key && !(p.data?.foreign_key_table_id) && !p.data?.is_deleted
      },
      { headerName: 'Display Length', field: 'display_length', width: 160, headerTooltip: 'Preferred display width (chars)',
        cellEditor: 'agNumberCellEditor'
      },
      { headerName: 'Description', field: 'description', minWidth: 240, headerTooltip: 'Helpful description for this column',
        valueFormatter: p => truncate(p.value, 50),
        cellEditor: 'agLargeTextCellEditor', cellEditorPopup: true,
        cellEditorParams: { rows: 4, cols: 80 }
      }
    ]
  }

  // Infinite datasource
  getDataSource() {
    return {
      getRows: (params) => {
        fetch(`/api/column_definitions/${this.tableDefinitionIdValue}/grid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startRow: params.startRow, endRow: params.endRow, filterModel: params.filterModel, sortModel: params.sortModel })
        })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()})
        .then(data => params.successCallback(data.rows, data.rowCount))
        .catch(err => { console.error('[col-defs] grid load error', err); this.showError(`Error loading data: ${err.message}`); params.failCallback(); })
      }
    }
  }

  // Load all rows once for client-side editing mode (enables drag reorder)
  loadAllRowsForEditing() {
    return fetch(`/api/column_definitions/${this.tableDefinitionIdValue}/grid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startRow: 0, endRow: 10000 })
    })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
    .then(data => {
      const rows = Array.isArray(data.rows) ? data.rows : []
      this.gridApi.setGridOption('rowData', rows)
      return rows
    })
    .catch(err => { this.showError(`Error loading rows: ${err.message}`); throw err })
  }

  // Editing enablement
  isEditable(p) { return !!this.editableValue && !p?.data?.is_deleted }

  // Identify string/text fields for strike-through styling when deleted
  isStringField(field) {
    return [
      'name',
      'data_type',
      'description',
      'default',
      'valid_values',
      'masking_strategy'
    ].includes(field)
  }
  lengthEnabledFor(dt) { return ["varchar","character","decimal","timestamp","timestamptz","time","timetz"].includes(dt) }
  scaleEnabledFor(dt) { return ["decimal"].includes(dt) }

  // Persist cell updates
  handleCellUpdate(event) {
    const { data, colDef, newValue, oldValue, node } = event
    if (node && node.rowPinned === 'top') {
      // enforce rules locally for add row, but don't persist
      this.applyEnablementRules(node, colDef.field)
      // local validation: scale must be <= length
      if (colDef.field === 'scale' || colDef.field === 'length') {
        const len = Number(node.data?.length)
        const sc = Number(node.data?.scale)
        if (Number.isFinite(len) && Number.isFinite(sc) && sc > len) {
          // revert the last edit locally
          if (typeof oldValue !== 'undefined') node.setDataValue(colDef.field, oldValue)
          this.showError('Scale must be less than or equal to Length')
        }
      }
      // refresh dependent columns visually
      this.gridApi?.refreshCells && this.gridApi.refreshCells({ force: true })
      return
    }
    if (!data || data.id == null) return
    if (newValue === oldValue) return

    // validation before persist: scale must be <= length
    if (colDef.field === 'scale' || colDef.field === 'length') {
      // node.data already holds the updated value
      const len = Number(node.data?.length)
      const sc = Number(node.data?.scale)
      if (Number.isFinite(len) && Number.isFinite(sc) && sc > len) {
        if (typeof oldValue !== 'undefined') node.setDataValue(colDef.field, oldValue)
        this.showError('Scale must be less than or equal to Length')
        return
      }
    }

    const payload = { record: { [colDef.field]: newValue } }
    fetch(`/api/column_definitions/${this.tableDefinitionIdValue}/${data.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    })
    .then(r => r.json().then(j=>({ok:r.ok, j})))
    .then(({ok, j}) => {
      if (!ok) throw new Error(j.errors?.join(', ') || j.error || 'Update failed')
      // After successful update, apply rules which may clear other fields
      this.applyEnablementRules(node, colDef.field)
      this.gridApi?.refreshCells && this.gridApi.refreshCells({ force: true })
      // Any edit means this table now requires promotion
      this.markRequiresPromotion()
      // Any column change should trigger deployment requirement
      this.markRequiresDeployment()
    })
    .catch(err => {
      console.error('[col-defs] update failed', err)
      // revert
      if (typeof oldValue !== 'undefined') node.setDataValue(colDef.field, oldValue)
      this.showError(`Update failed: ${err.message}`)
    })
  }

  // Apply original form enable/disable rules to the row data and clear conflicting fields
  applyEnablementRules(node, changedField = null) {
    if (!node) return
    const d = node.data || {}
    const isImported = !!d.is_imported
    const isPrimary = !!d.is_primary_key
    const hasFKey = !!d.foreign_key_table_id
    const hasValidValues = !!(d.valid_values && String(d.valid_values).trim().length > 0)

    // If imported or primary: clear mutually dependent fields
    if (isImported || isPrimary) {
      if (d.valid_values) node.setDataValue('valid_values', '')
      if (d.foreign_key_table_id) node.setDataValue('foreign_key_table_id', null)
      if (d.default != null && d.default !== '') node.setDataValue('default', null)
    }

    // If primary: required true and disable mask (clear)
    if (isPrimary) {
      if (!d.is_required) node.setDataValue('is_required', true)
      if (d.masking_strategy) node.setDataValue('masking_strategy', '')
    }

    // Mutual exclusion between valid_values and foreign_key_table_id
    if (hasFKey && hasValidValues) {
      // FKey takes precedence: clear valid_values
      node.setDataValue('valid_values', '')
    } else if (!hasFKey && hasValidValues) {
      // Ensure FKey cleared if values present
      if (d.foreign_key_table_id) node.setDataValue('foreign_key_table_id', null)
    }

    // When a FK is set, force data_type to varchar and clear length/scale
    if (hasFKey) {
      if (d.data_type !== 'text') node.setDataValue('data_type', 'text')
      if (d.length != null && d.length !== '') node.setDataValue('length', null)
      if (d.scale != null && d.scale !== '') node.setDataValue('scale', null)
    }
  }

  // On edit stop, just refresh the add icon state (no auto-create)
  handlePinnedRowEditStopped(event) {
    if (!this.editableValue) return
    if (!event?.node || event.node.rowPinned !== 'top') return
    // Re-render the drag column for pinned row so the + icon reflects readiness
    if (this.gridApi?.refreshCells) this.gridApi.refreshCells({ force: true, columns: ['drag','name'] })
  }

  // Update the promotion status chip to "Requires Promotion" (amber)
  markRequiresPromotion() {
    // Branches without a parent have nothing to promote to (no Promote link is rendered)
    if (!this.hasPromoteLinkTarget) return
    if (this.hasPromotionStatusTarget) {
      this.promotionStatusTarget.innerHTML = `
        <span class="inline-flex items-center rounded-md bg-amber-100 text-amber-800 px-2 py-0.5 text-sm font-medium ring-1 ring-inset ring-amber-200">Requires Promotion</span>
      `
    }
    // Also enable the Promote action by toggling controls
    if (this.hasPromoteLinkTarget) {
      this.promoteLinkTarget.classList.remove('hidden')
    }
    if (this.hasPromoteDisabledTarget) {
      this.promoteDisabledTarget.classList.add('hidden')
    }
  }

  // Update deployment requirement indicators
  markRequiresDeployment() {
    if (this.hasRequiresDeploymentChipTarget) {
      this.requiresDeploymentChipTarget.classList.remove('hidden')
    }
    if (this.hasDeployButtonTarget) {
      this.deployButtonTarget.classList.remove('hidden')
    }
  }

  // Create from pinned top row when user clicks the + icon
  createFromPinnedRow(pinnedData = null) {
    if (!this.editableValue) return
    // Guard: if a create is already in-flight, ignore
    if (this._creatingPinned) return
    // Commit any in-progress edit so latest values are in the row data
    if (this.gridApi?.stopEditing) this.gridApi.stopEditing(true)
    const newRow = pinnedData || {}
    if (!newRow || !newRow.name || !newRow.data_type) {
      // not ready; ensure guard remains false
      this._creatingPinned = false
      return
    }
    this._creatingPinned = true
    // Build payload with only permitted attributes
    const record = {
      name: (newRow.name || '').toString().trim(),
      data_type: newRow.data_type,
      length: newRow.length ?? null,
      scale: newRow.scale ?? null,
      is_imported: !!newRow.is_imported,
      is_primary_key: !!newRow.is_primary_key,
      is_required: !!newRow.is_required,
      foreign_key_table_id: newRow.foreign_key_table_id ?? null,
      default: newRow.default ?? null,
      valid_values: newRow.valid_values ?? null,
      masking_strategy: newRow.masking_strategy ?? null,
      display_length: newRow.display_length ?? null,
      description: newRow.description ?? null,
      display_order: newRow.display_order ?? null
    }
    console.debug('[col-defs] creating', record)
    return fetch(`/api/column_definitions/${this.tableDefinitionIdValue}/new`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ record })
    })
    .then(async (r) => {
      const text = await r.text()
      let json = null
      try { json = text ? JSON.parse(text) : null } catch (_) {}
      if (!r.ok) {
        const msg = json?.errors?.join(', ') || json?.error || `HTTP ${r.status}`
        throw new Error(msg)
      }
      // Success
      const created = json?.record || null
      console.debug('[col-defs] created response', created)
      // Reset pinned row for next entry
      this.gridApi.setGridOption('pinnedTopRowData', [{}])
      if (created && created.id != null) {
        // Insert immediately for snappy UX
        if (this.gridApi.applyTransaction) {
          this.gridApi.applyTransaction({ add: [created] })
        }
      }
      // Any create means this table now requires promotion and deployment
      this.markRequiresPromotion()
      this.markRequiresDeployment()
      // Also refresh full data to ensure correct ordering and server truth
      if (this.editableValue) {
        this.loadAllRowsForEditing().then((rows) => {
          if (this.gridApi.refreshCells) this.gridApi.refreshCells({ force: true })
          // Try to reveal the created row
          if (created && created.id != null) {
            let targetIndex = -1
            this.gridApi.forEachNode((node) => {
              if (targetIndex >= 0) return
              if (node.data?.id === created.id) targetIndex = node.rowIndex
            })
            if (targetIndex >= 0 && this.gridApi.ensureIndexVisible) this.gridApi.ensureIndexVisible(targetIndex)
          }
        })
      } else if (this.gridApi.refreshInfiniteCache) {
        this.gridApi.refreshInfiniteCache()
      }
    })
    .catch(err => this.showError(`Create failed: ${err.message}`))
    .finally(() => { this._creatingPinned = false })
  }

  // Row reorder on drag end
  handleRowReorder(event) {
    // Only allow when editing and not pinned rows
    if (!this.editableValue) return
    const moved = event.node?.data
    const rowIndex = event.node?.rowIndex
    if (!moved || moved.id == null || typeof rowIndex !== 'number') return
    // Convert to 1-based for insert_at
    const position = rowIndex + 1
    fetch(`/api/column_definitions/${this.tableDefinitionIdValue}/${moved.id}/reorder`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ position })
    }).then(() => {
      // For client-side model, no cache to refresh; just ensure indexes are updated
      if (this.gridApi.refreshCells) this.gridApi.refreshCells({ force: true })
      // Any reorder means this table now requires promotion
      this.markRequiresPromotion()
    }).catch(err => this.showError(`Reorder failed: ${err.message}`))
  }

  // Handle action clicks
  onCellClicked(params) {
    const colId = params.colDef?.colId
    if (colId !== 'actions' && colId !== 'drag') return
    const btn = params.event?.target?.closest('button')
    if (!btn) return
    
    // Prevent delete/restore actions in read-only mode
    if (!this.editableValue && (btn.dataset.action === 'delete' || btn.dataset.action === 'restore')) {
      return
    }
    // Handle pinned row (+) create: commit edits then create if ready
    if (params.node?.rowPinned === 'top') {
      if (this.gridApi?.stopEditing) this.gridApi.stopEditing(true)
      // After committing, if name and data_type are present, proceed to create
      let pinned = null
      if (this.gridApi?.getPinnedTopRow) pinned = this.gridApi.getPinnedTopRow(0)
      if (!pinned) this.gridApi?.forEachNode((node) => { if (!pinned && node.rowPinned === 'top') pinned = node })
      const data = pinned?.data || {}
      if (data.name && data.data_type) {
        this.createFromPinnedRow(data)
      } else {
        // Refresh icon state so user sees readiness
        if (this.gridApi?.refreshCells) this.gridApi.refreshCells({ force: true, columns: ['drag'] })
      }
      return
    }
    if (btn.dataset.action === 'delete') {
      // Guard: prevent delete for primary key columns
      if (params.data?.is_primary_key) {
        this.showError('Cannot delete a primary key column')
        return
      }
      const name = params.data?.name || 'this column'
      this.showConfirmModal({
        title: 'Delete column',
        message: `Are you sure you want to delete column definition “${name}”? This column definition can be restored again, before promotion.`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }).then((ok) => {
        if (!ok) return
      fetch(`/api/column_definitions/${this.tableDefinitionIdValue}/${params.data.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ record: { is_deleted: true } })
      }).then(async (r) => {
        const text = await r.text()
        let json = null
        try { json = text ? JSON.parse(text) : null } catch (_) { /* noop */ }
        if (!r.ok) {
          const msg = json?.errors?.join(', ') || json?.error || `HTTP ${r.status}`
          throw new Error(msg)
        }
        const newData = { ...(params.node.data || {}), is_deleted: true }
        params.node.setData(newData)
        // Force redraw to ensure immediate visual update
        if (params.api.redrawRows) params.api.redrawRows({ rowNodes: [params.node] })
        if (params.api.refreshCells) params.api.refreshCells({ rowNodes: [params.node], columns: ['name','drag'], force: true })
        // Mark table as requiring promotion
        this.markRequiresPromotion()
        this.markRequiresDeployment()
      })
      .catch(err => this.showError(`Delete failed: ${err.message}`))
    })
  }
  if (btn.dataset.action === 'restore') {
    // Restore soft-deleted column
    fetch(`/api/column_definitions/${this.tableDefinitionIdValue}/${params.data.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ record: { is_deleted: false } })
    }).then(async (r) => {
      const text = await r.text()
      let json = null
      try { json = text ? JSON.parse(text) : null } catch (_) { /* noop */ }
      if (!r.ok) {
        const msg = json?.errors?.join(', ') || json?.error || `HTTP ${r.status}`
        throw new Error(msg)
      }
      const newData = { ...(params.node.data || {}), is_deleted: false }
      params.node.setData(newData)
      if (params.api.redrawRows) params.api.redrawRows({ rowNodes: [params.node] })
      if (params.api.refreshCells) params.api.refreshCells({ rowNodes: [params.node], columns: ['name','drag'], force: true })
      this.markRequiresPromotion()
    })
    .catch(err => this.showError(`Restore failed: ${err.message}`))
  }
}

// Display a Rails-style flash message at the top of the page.
// type: 'notice' | 'alert'
showFlash(type, message) {
  try {
    const body = document.body
    if (!body) return console.error('[flash] body not available')

    // Create (or reuse) a fixed overlay container like the partial
    let overlay = document.getElementById('ruinew-flash-container')
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.id = 'ruinew-flash-container'
      overlay.className = 'fixed inset-x-0 top-4 z-50 flex justify-center px-2 pointer-events-none'
      overlay.setAttribute('aria-live', 'polite')
      overlay.setAttribute('aria-atomic', 'true')
      // Insert at top of body
      if (body.firstChild) body.insertBefore(overlay, body.firstChild)
      else body.appendChild(overlay)
    }

    // Build inner alert box matching the partial
    const box = document.createElement('div')
    box.setAttribute('role', 'alert')
    box.dataset.controller = 'alert'
    box.className = 'pointer-events-auto max-w-xl w-full rounded-md shadow-md ring-1 ring-black/10 text-white px-4 py-3'
    if (type === 'notice') {
      box.classList.add('bg-secondary-600')
    } else {
      box.classList.add('bg-red-600', 'relative')
      box.dataset.alertAutoDismissValue = 'false'
      // Dismiss button (also wire up JS in case Stimulus controller isn't present)
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'absolute right-2 top-2 inline-flex items-center justify-center rounded p-1 text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40'
      btn.setAttribute('aria-label', 'Dismiss alert')
      btn.dataset.action = 'click->alert#dismiss'
      btn.innerHTML = '<span class="sr-only">Dismiss</span>&times;'
      btn.addEventListener('click', () => {
        box.remove()
        if (overlay.childElementCount === 0) overlay.remove()
      })
      box.appendChild(btn)
    }

    const p = document.createElement('p')
    p.className = type === 'notice' ? 'text-sm' : 'text-sm pr-6'
    p.textContent = message
    box.appendChild(p)
    overlay.appendChild(box)

    // Auto-dismiss only notices (alerts persist like the partial)
    if (type === 'notice') {
      setTimeout(() => {
        box.remove()
        if (overlay.childElementCount === 0) overlay.remove()
      }, 8000)
    }
  } catch (e) {
    console.error('[flash] failed to show message', e, message)
  }
}

showError(message) {
  // Route errors to page-level flash (alert)
  this.showFlash('alert', message)
}

// ----- Modal utilities -----
ensureModalElements() {
  if (this._modalRoot) return
  const root = document.createElement('div')
  root.id = 'col-defs-confirm-modal'
  root.className = 'fixed inset-0 z-50 hidden'
  root.innerHTML = `
    <div class="absolute inset-0 bg-gray-900/50"></div>
    <div class="absolute inset-0 flex items-center justify-center p-4">
      <div class="w-full max-w-md rounded-lg bg-white shadow-xl ring-1 ring-black/5">
        <div class="px-5 py-4 border-b border-gray-100">
          <h3 class="text-base font-semibold text-gray-900" data-role="title">Confirm</h3>
        </div>
        <div class="px-5 py-4">
          <p class="text-sm text-gray-700" data-role="message">Are you sure?</p>
        </div>
        <div class="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button type="button" data-role="cancel" class="inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-gray-800 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300">Cancel</button>
          <button type="button" data-role="confirm" class="inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">Delete</button>
        </div>
      </div>
    `
    document.body.appendChild(root)
    this._modalRoot = root
    this._modalTitle = root.querySelector('[data-role="title"]')
    this._modalMessage = root.querySelector('[data-role="message"]')
    this._modalCancel = root.querySelector('[data-role="cancel"]')
    this._modalConfirm = root.querySelector('[data-role="confirm"]')
  }

  showConfirmModal({ title = 'Confirm', message = 'Are you sure?', confirmText = 'Confirm', cancelText = 'Cancel' } = {}) {
    this.ensureModalElements()
    const root = this._modalRoot
    const titleEl = this._modalTitle
    const msgEl = this._modalMessage
    const cancelBtn = this._modalCancel
    const confirmBtn = this._modalConfirm

    titleEl.textContent = title
    msgEl.textContent = message
    confirmBtn.textContent = confirmText
    cancelBtn.textContent = cancelText

    return new Promise((resolve) => {
      const cleanup = () => {
        cancelBtn.removeEventListener('click', onCancel)
        confirmBtn.removeEventListener('click', onConfirm)
        document.removeEventListener('keydown', onKey)
        root.classList.add('hidden')
      }
      const onCancel = () => { cleanup(); resolve(false) }
      const onConfirm = () => { cleanup(); resolve(true) }
      const onKey = (e) => { if (e.key === 'Escape') onCancel() }
      cancelBtn.addEventListener('click', onCancel)
      confirmBtn.addEventListener('click', onConfirm)
      document.addEventListener('keydown', onKey)
      root.classList.remove('hidden')
    })
  }
}
